import React, { FC, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faShare, faStopwatch, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { useIntl } from "react-intl";

import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import { EntityTypeSupportingGroups, Group } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import BulkApiError from "@mapmycustomers/shared/util/api/BulkApiError";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import CustomFrequencySelector from "@app/component/frequency/FrequencySelector/CustomFrequencySelector";
import SharingModal from "@app/scene/map/components/SharePicker/components/SharingModal";
import { getGroupsForEntity, groupsUpdatingInBulk } from "@app/store/groups";
import {
  bulkDeleteGroups,
  bulkUpdateGroups,
  bulkUpdateGroupSharing,
} from "@app/store/groups/actions";
import { RootState } from "@app/store/rootReducer";

import styles from "./BulkActions.module.scss";
import messages from "./messages";
import showBulkDeleteErrorModal from "./showBulkDeleteErrorModal";

export interface BulkActionsProps {
  entityType: EntityTypeSupportingGroups;
  groups: Group[];
  onBulkUpdateGroups: typeof bulkUpdateGroups.request;
  onDeleteGroups: typeof bulkDeleteGroups.request;
  onUpdateGroupsSharing: typeof bulkUpdateGroupSharing.request;
  selectedGroupIds: Group["id"][];
  updating: boolean;
}

const BulkActions: FC<BulkActionsProps> = ({
  entityType,
  groups,
  onBulkUpdateGroups,
  onDeleteGroups,
  onUpdateGroupsSharing,
  selectedGroupIds,
  updating,
}) => {
  const intl = useIntl();
  const [frequencyPopoverVisible, setFrequencyPopoverVisible] = useState<boolean>(false);
  const [sharingModalVisible, showSharingModal, hideSharingModal] = useBoolean();

  const count = selectedGroupIds.length;

  const selectedGroups = useMemo(
    () => groups.filter(({ id }) => selectedGroupIds.includes(id)),
    [groups, selectedGroupIds]
  );

  const singleSelectedGroup = useMemo(
    () => (selectedGroups.length === 1 ? selectedGroups[0] : undefined),
    [selectedGroups]
  );

  const handleDeleteGroups = useCallback(() => {
    onDeleteGroups({
      entityType,
      failureCallback: (validationErrors: BulkApiError[]) => {
        showBulkDeleteErrorModal(intl, selectedGroups, validationErrors);
      },
      groups: selectedGroups,
    });
  }, [entityType, intl, onDeleteGroups, selectedGroups]);

  const handleUpdateFrequency = useCallback(
    ({ cadenceInterval, crmActivityTypeId }: Partial<Cadenced>) => {
      onBulkUpdateGroups({
        entityType,
        groups: selectedGroups.map(({ id }) => ({
          id,
          cadenceInterval,
          crmActivityTypeId,
        })),
      });
    },
    [entityType, onBulkUpdateGroups, selectedGroups]
  );

  const handleShareGroups = useCallback(
    (userIds: User["id"][]) => {
      onUpdateGroupsSharing({ entityType, groups: selectedGroups, userIds });
    },
    [entityType, onUpdateGroupsSharing, selectedGroups]
  );

  return (
    <>
      <Popover
        content={
          <CustomFrequencySelector
            isGroup
            onHide={() => setFrequencyPopoverVisible(false)}
            onUpdate={handleUpdateFrequency}
          />
        }
        onOpenChange={setFrequencyPopoverVisible}
        open={frequencyPopoverVisible}
        overlayClassName={styles.frequencyPopover}
        placement="bottom"
        title={intl.formatMessage(messages.manageFrequency, { count })}
        trigger={updating ? [] : ["click"]}
      >
        <Button disabled={updating} leftIcon={<FontAwesomeIcon icon={faStopwatch} />} type="filled">
          {intl.formatMessage(messages.setFrequency)}
        </Button>
      </Popover>
      <Button
        disabled={updating}
        leftIcon={<FontAwesomeIcon icon={faShare} />}
        onClick={showSharingModal}
        type="filled"
      >
        {intl.formatMessage(messages.share)}
      </Button>
      {sharingModalVisible && (
        <SharingModal
          count={count}
          creatorUserId={singleSelectedGroup?.user.id}
          isGroupSharing
          onChange={handleShareGroups}
          onHide={hideSharingModal}
          userIds={singleSelectedGroup?.userIds ?? []}
        />
      )}
      <Popconfirm
        danger
        description={intl.formatMessage(messages.deleteGroupsTitle, { count })}
        okText={intl.formatMessage(messages.deleteGroupButton, { count })}
        onConfirm={handleDeleteGroups}
        placement="bottom"
        trigger={updating ? [] : ["click"]}
      >
        <Button
          danger
          disabled={updating}
          leftIcon={<FontAwesomeIcon icon={faTrash} />}
          type="filled"
        >
          {intl.formatMessage(messages.delete)}
        </Button>
      </Popconfirm>
    </>
  );
};

const mapStateToProps = (
  state: RootState,
  { entityType }: { entityType: EntityTypeSupportingGroups }
) => ({
  groups: getGroupsForEntity(state)(entityType),
  updating: groupsUpdatingInBulk(state),
});

const mapDispatchToProps = {
  onBulkUpdateGroups: bulkUpdateGroups.request,
  onDeleteGroups: bulkDeleteGroups.request,
  onUpdateGroupsSharing: bulkUpdateGroupSharing.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkActions);
